import { Blockquote, Float, HStack, Table } from "@chakra-ui/react"
import { Heading } from "@chakra-ui/react"
import { Text } from "@chakra-ui/react"
import { Checkbox } from "@chakra-ui/react"
import { Box } from "@chakra-ui/react/box"
import { Code } from "@chakra-ui/react/code"
import { Image } from "@chakra-ui/react/image"
import { Link } from "@chakra-ui/react/link"
import { List } from "@chakra-ui/react/list"
import { Separator } from "@chakra-ui/react/separator"
import { h } from "hastscript"
import dynamic from "next/dynamic"
import React, { type FC } from "react"
import ReactMarkdown from "react-markdown"
import rehypeFormat from "rehype-format"
import rehypeRaw from "rehype-raw"
import {
  defListHastHandlers,
  remarkDefinitionList,
} from "remark-definition-list"
import remarkDirective from "remark-directive"
import remarkEmoji from "remark-emoji"
import remarkFlexibleMarkers from "remark-flexible-markers"
import remarkGfm from "remark-gfm"
import remarkIns from "remark-ins"
import supersub from "remark-supersub"
import { visit } from "unist-util-visit"

import ClientZoom from "@/components/ClientZoom"
import NextLink from "@/components/RouteTransitionLink"

// Dynamically load the syntax highlighter (and its Prism+languages payload)
// only when an article actually contains a fenced code block. This keeps
// `react-syntax-highlighter` + `refractor` (~1.4 MB) out of the initial chunk.
// Note: SSR stays on (this file is consumed from server components too); the
// dynamic boundary still gives webpack a separate async chunk.
const CodeBlock = dynamic(() => import("./CodeBlock"), {
  loading: () => null,
})

type SyntaxHighlighterProps = Record<string, unknown>

const markdownDefault = `
## _The Last Markdown Editor, Ever_
`

interface MarkdownParserProps {
  markdown?: string | null
  color?: string
  marginTop?: string | number
  marginBottom?: string | number
  ulProps?: React.ComponentProps<typeof List.Root>
  olProps?: React.ComponentProps<typeof List.Root>
  liProps?: React.ComponentProps<typeof List.Item>
  headingProps?: React.ComponentProps<typeof Heading>
  pProps?: React.ComponentProps<typeof Text>
  imgProps?: React.ComponentProps<typeof Image>
  imgContainerProps?: React.ComponentProps<typeof HStack>
  delProps?: React.ComponentProps<typeof Text>
  emProps?: React.ComponentProps<typeof Text>
  strongProps?: React.ComponentProps<typeof Text>
  aProps?: React.ComponentProps<typeof Link>
  hrProps?: React.ComponentProps<typeof Separator>
  codeProps?: SyntaxHighlighterProps
  codeContainerProps?: React.ComponentProps<typeof Box>
  blockquoteProps?: React.ComponentProps<typeof Blockquote.Root>
  tableProps?: React.ComponentProps<typeof Table.Root>
  theadProps?: React.ComponentProps<typeof Table.Header>
  tbodyProps?: React.ComponentProps<typeof Table.Body>
  trProps?: React.ComponentProps<typeof Table.Row>
  thProps?: React.ComponentProps<typeof Table.ColumnHeader>
  tdProps?: React.ComponentProps<typeof Table.Cell>
  fontSize?: React.ComponentProps<typeof Text>["fontSize"]
  preserveDoubleEmptyLines?: boolean
  proseProps?: React.ComponentProps<typeof Box>
}

interface ComponentProps {
  node?: any
  inline?: boolean
  className?: string
  children?: React.ReactNode
  href?: string
  cite?: string
  src?: string | Blob
  alt?: string
  title?: string
  ordered?: boolean
  checked?: boolean
  depth?: number
}

type GetCoreProps = {
  children?: React.ReactNode
  "data-sourcepos"?: any
}

function getCoreProps(props: GetCoreProps): any {
  return props["data-sourcepos"]
    ? { "data-sourcepos": props["data-sourcepos"] }
    : {}
}

function rehypeStripScriptTags() {
  return (tree: unknown) => {
    visit(
      tree as any,
      "element",
      (node: any, index: number | undefined, parent: any) => {
        if (!parent || typeof index !== "number") {
          return
        }

        if (node?.tagName === "script") {
          parent.children.splice(index, 1, {
            children: [],
            properties: {
              "data-ignored-script": "",
            },
            tagName: "template",
            type: "element",
          })
        }
      }
    )
  }
}

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

function remarkAbbreviations() {
  return (tree: any) => {
    const definitions = new Map<string, string>()

    visit(tree, "paragraph", (node: any, index, parent: any) => {
      if (!parent || typeof index !== "number") {
        return
      }

      if (node.children?.length !== 1 || node.children[0]?.type !== "text") {
        return
      }

      const value = String(node.children[0].value || "").trim()
      const match = value.match(/^\*\[([^\]]+)]:\s*(.+)$/)

      if (!match) {
        return
      }

      definitions.set(match[1]!, match[2]!)
      parent.children.splice(index, 1)
    })

    if (definitions.size === 0) {
      return
    }

    const abbreviations = [...definitions.keys()].toSorted(
      (a, b) => b.length - a.length
    )
    const abbreviationPattern = new RegExp(
      `\\b(${abbreviations.map(escapeRegExp).join("|")})\\b`,
      "g"
    )

    visit(tree, "text", (node: any, index, parent: any) => {
      if (!parent || typeof index !== "number") {
        return
      }

      const value = String(node.value || "")

      const matches = [...value.matchAll(abbreviationPattern)]

      if (matches.length === 0) {
        return
      }

      const children: any[] = []
      let lastIndex = 0

      for (const match of matches) {
        const abbreviation = match[0]

        if (match.index > lastIndex) {
          children.push({
            type: "text",
            value: value.slice(lastIndex, match.index),
          })
        }

        children.push({
          children: [{ type: "text", value: abbreviation }],
          data: {
            hName: "abbr",
            hProperties: {
              title: definitions.get(abbreviation),
            },
          },
          type: "abbr",
        })

        lastIndex = match.index + abbreviation.length
      }

      if (lastIndex < value.length) {
        children.push({ type: "text", value: value.slice(lastIndex) })
      }

      parent.children.splice(index, 1, ...children)
    })
  }
}

const inlineDirectiveTags = new Set([
  "abbr",
  "cite",
  "ins",
  "kbd",
  "mark",
  "samp",
  "small",
  "span",
  "sub",
  "sup",
  "var",
])

function remarkCustomDirectives() {
  return (tree: unknown) => {
    visit(tree as any, (node: any) => {
      if (
        node.type !== "containerDirective" &&
        node.type !== "leafDirective" &&
        node.type !== "textDirective"
      ) {
        return
      }

      const data = node.data || (node.data = {})
      const attributes = node.attributes || {}
      const directiveName = String(node.name || "container")
      const safeDirectiveName = directiveName
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, "-")

      if (node.type === "textDirective") {
        const tagName = inlineDirectiveTags.has(safeDirectiveName)
          ? safeDirectiveName
          : "span"
        const hast = h(tagName, {
          ...attributes,
          className: [
            "markdown-directive",
            `markdown-directive-${safeDirectiveName}`,
          ],
        })

        data.hName = hast.tagName
        data.hProperties = hast.properties
        return
      }

      if (node.type === "leafDirective") {
        const tagName =
          safeDirectiveName === "br"
            ? "br"
            : safeDirectiveName === "hr"
              ? "hr"
              : "div"
        const hast = h(tagName, {
          ...attributes,
          className: [
            "markdown-directive",
            `markdown-directive-${safeDirectiveName}`,
          ],
          "data-directive": safeDirectiveName,
        })

        data.hName = hast.tagName
        data.hProperties = hast.properties
        return
      }

      const hast = h("div", {
        ...attributes,
        className: [
          "markdown-custom-container",
          `markdown-custom-container-${safeDirectiveName}`,
        ],
        "data-container": safeDirectiveName,
      })

      data.hName = hast.tagName
      data.hProperties = hast.properties
    })
  }
}

function preserveExtraBlankLines(markdown: string) {
  return markdown.replace(/\r?\n(?:[ \t]*\r?\n){2,}/g, (match) => {
    const newlineCount = match.match(/\r?\n/g)?.length ?? 0
    const extraBlankLines = Math.max(1, newlineCount - 2)

    return `\n\n${'<div data-markdown-blank-line=""></div>\n\n'.repeat(extraBlankLines)}`
  })
}

function getTextContentFromHast(node: any): string {
  if (!node) {
    return ""
  }

  if (node.type === "text") {
    return String(node.value || "")
  }

  if (Array.isArray(node.children)) {
    return node.children.map(getTextContentFromHast).join("")
  }

  return ""
}

function splitMarkdownImageAlt(alt: string) {
  const match = alt.match(/^(.*?)\s+\|\s+(.+)$/)

  if (!match) {
    return {
      alt,
      caption: "",
    }
  }

  return {
    alt: match[1]?.trim() || alt,
    caption: match[2]?.trim() || "",
  }
}

const MarkdownParser: FC<MarkdownParserProps> = ({
  aProps = undefined,
  blockquoteProps = undefined,
  codeContainerProps = undefined,
  codeProps = undefined,
  color = undefined,
  delProps = undefined,
  emProps = undefined,
  fontSize = undefined,
  headingProps = undefined,
  hrProps = undefined,
  imgContainerProps = undefined,
  imgProps = undefined,
  liProps = undefined,
  marginBottom = undefined,
  marginTop = undefined,
  markdown = markdownDefault,
  olProps = undefined,
  pProps = undefined,
  preserveDoubleEmptyLines = true,
  proseProps = undefined,
  strongProps = undefined,
  tableProps = undefined,
  tbodyProps = undefined,
  tdProps = undefined,
  theadProps = undefined,
  thProps = undefined,
  trProps = undefined,
  ulProps = undefined,
}) => {
  const normalizedMarkdown = markdown ?? ""
  const renderedMarkdown = preserveDoubleEmptyLines
    ? preserveExtraBlankLines(normalizedMarkdown)
    : normalizedMarkdown
  const renderMarkdownImage = (
    props: ComponentProps,
    captionOverride?: string
  ) => {
    const imgSrc = typeof props.src === "string" ? props.src : ""
    const { alt, caption: altCaption } = splitMarkdownImageAlt(
      typeof props.alt === "string" ? props.alt : ""
    )
    const titleCaption =
      typeof props.title === "string" ? props.title.trim() : ""
    const caption = captionOverride?.trim() || titleCaption || altCaption
    const image = (
      <HStack
        as="div"
        justifyContent="center"
        py={caption ? 0 : 4}
        {...imgContainerProps}
        className="markdown-image-container"
      >
        <ClientZoom>
          <Image
            alt={alt}
            borderRadius="2xl"
            src={imgSrc + "?quality=100&width=1438&format=webp"}
            title={props.title}
            {...imgProps}
          />
        </ClientZoom>
      </HStack>
    )

    if (caption) {
      return (
        <Box as="figure" my={4} w="full">
          {image}
          <Text
            as="figcaption"
            color="fg.muted"
            fontSize="sm"
            mt={2}
            textAlign="center"
          >
            {caption}
          </Text>
        </Box>
      )
    }

    return image
  }

  return (
    <Box
      className="markdown-prose"
      color={color || "fg.read"}
      css={{
        "& .markdown-custom-container": {
          background: "{colors.bg.subtle}",
          borderColor: "{colors.border}",
          borderLeftColor: "{colors.primary.500}",
          borderLeftWidth: "4px",
          borderRadius: "{radii.md}",
          borderWidth: "1px",
          marginBottom: "1em",
          marginTop: "1em",
          padding: "1rem",
        },
        "& > :first-child": {
          marginTop: 0,
        },
        "& > :last-child": {
          marginBottom: 0,
        },
        "& > h1:not(:first-child), & > h2:not(:first-child), & > h3:not(:first-child), & > h4:not(:first-child), & > h5:not(:first-child), & > h6:not(:first-child)":
          {
            marginTop: "1.6em",
          },
        "& > ol + p, & > ul + p, & > p + ol, & > p + ul": {
          marginTop: "0.75em",
        },
        "& > p + p": {
          marginTop: "0.85em",
        },
      }}
      fontFamily="body"
      fontSize={fontSize || "md"}
      lineHeight="1.65"
      width="100%"
      {...proseProps}
    >
      <ReactMarkdown
        components={{
          a: ({ node, ...props }: ComponentProps) => {
            const href = props.href || "#"
            const isExternalHref = /^(?:https?:|mailto:|tel:)/.test(href)

            return (
              /*@ts-ignore*/
              <Link
                asChild
                color={{ base: "primary.500", _dark: "primary.200" }}
                fontFamily="body"
                fontSize={fontSize || "md"}
                gap={"2px"}
                marginBottom={marginBottom || 0}
                marginTop={marginTop || 0}
                {...aProps}
              >
                <NextLink
                  href={href}
                  target={isExternalHref ? "_blank" : undefined}
                  {...props}
                >
                  {props.children}
                </NextLink>
              </Link>
            )
          },
          abbr: ({ node, ...props }: ComponentProps) => (
            <Text
              as="abbr"
              borderBottom="1px dotted"
              borderColor="border.emphasized"
              color={color || "fg.read"}
              cursor="help"
              fontSize={fontSize || "md"}
              title={props.title}
              {...props}
            />
          ),
          blockquote: ({ cite, node, ...props }: ComponentProps) => (
            <Blockquote.Root
              _after={{
                bgColor: { base: "primary.500", _dark: "primary.400" },
                bottom: "-1px",
                content: '""',
                left: "-4px",
                position: "absolute",
                top: "-1px",
                width: "4px",
              }}
              bg="bg.subtle"
              border={{ base: "1px solid {colors.border}" }}
              borderLeft={"1px solid transparent"}
              colorPalette="primary"
              fontFamily="mono"
              fontStyle={"italic"}
              marginBottom={marginBottom || 4}
              marginTop={marginTop || 2}
              padding="6"
              position="relative"
              pr={14}
              {...props}
              {...blockquoteProps}
            >
              <Float offset="7" placement="bottom-end">
                <Blockquote.Icon boxSize="10" opacity="0.4" rotate="180deg" />
              </Float>
              <Blockquote.Content cite={cite} css={{ "& p": { margin: 0 } }}>
                {props.children}
              </Blockquote.Content>
            </Blockquote.Root>
          ),
          code({
            children,
            className,
            inline,
            node,
            ...props
          }: ComponentProps) {
            const match = /language-(\w+)/.exec(className || "")
            const language = match?.[1]
            return !inline && language ? (
              <CodeBlock
                containerProps={codeContainerProps}
                highlighterProps={{ ...props, ...codeProps }}
                language={language}
              >
                {children}
              </CodeBlock>
            ) : (
              <Code
                fontFamily="code"
                fontSize={fontSize || "0.85em"}
                saturate={"lg"}
                // colorpalette="gray"
                variant={"subtle"}
                {...props}
              >
                {children}
              </Code>
            )
          },
          dd: ({ node, ...props }: ComponentProps) => (
            <Box
              as="dd"
              color={color || "fg.read"}
              fontFamily="body"
              fontSize={fontSize || "md"}
              lineHeight="1.65"
              mb={3}
              ml={6}
              {...props}
            />
          ),
          del: (props) => {
            const { children } = props
            return (
              <Text as="del" {...delProps}>
                {children}
              </Text>
            )
          },
          div: ({ node, ...props }: ComponentProps) => {
            const isBlankLine =
              (props as Record<string, unknown>)["data-markdown-blank-line"] !==
              undefined
            const className = String(props.className || "")
            const isCustomContainer = className.includes(
              "markdown-custom-container"
            )

            if (isBlankLine) {
              return (
                <Box
                  aria-hidden="true"
                  data-markdown-blank-line=""
                  height="1em"
                  width="100%"
                />
              )
            }

            return (
              <Box
                as="div"
                color={color || "fg.read"}
                fontSize={fontSize || "md"}
                lineHeight="1.65"
                {...(isCustomContainer
                  ? {
                      mb: 4,
                      mt: 4,
                    }
                  : {})}
                {...props}
              />
            )
          },
          dl: ({ node, ...props }: ComponentProps) => (
            <Box
              as="dl"
              color={color || "fg.read"}
              display="grid"
              fontSize={fontSize || "md"}
              gap={1}
              my={4}
              {...props}
            />
          ),
          dt: ({ node, ...props }: ComponentProps) => (
            <Text
              as="dt"
              color={color || "fg"}
              fontFamily="body"
              fontSize={fontSize || "md"}
              fontWeight="bold"
              lineHeight="1.45"
              {...props}
            />
          ),
          em: ({ node, ...props }: ComponentProps) => (
            <Text
              as="em"
              fontFamily="body"
              fontSize={fontSize || "md"}
              marginBottom={marginBottom || "0"}
              {...props}
              {...emProps}
            />
          ),
          figcaption: ({ node, ...props }: ComponentProps) => (
            <Text
              as="figcaption"
              color="fg.muted"
              fontSize="sm"
              mt={2}
              textAlign="center"
              {...props}
            />
          ),
          h1: ({ node, ...props }: ComponentProps) => (
            <Heading
              as="h1"
              color={color || "fg"}
              fontFamily="heading"
              lineHeight="1.05"
              marginBottom={marginBottom || "0.35em"}
              marginTop={marginTop || 0}
              size={"2xl"}
              textWrapMode="initial"
              textWrapStyle="pretty"
              wordBreak="normal"
              wordWrap="initial"
              {...props}
              {...headingProps}
            />
          ),
          h2: ({ node, ...props }: ComponentProps) => (
            <Heading
              as="h2"
              color={color || "fg"}
              fontFamily="heading"
              lineHeight="1.1"
              marginBottom={marginBottom || "0.35em"}
              marginTop={marginTop || 0}
              size={"xl"}
              textWrapMode="initial"
              textWrapStyle="pretty"
              wordBreak="normal"
              wordWrap="initial"
              {...props}
              {...headingProps}
            />
          ),
          h3: ({ node, ...props }: ComponentProps) => (
            <Heading
              as="h3"
              color={color || "fg"}
              fontFamily="heading"
              lineHeight="1.15"
              marginBottom={marginBottom || "0.3em"}
              marginTop={marginTop || 0}
              size={"lg"}
              textWrapMode="initial"
              textWrapStyle="pretty"
              wordBreak="normal"
              wordWrap="initial"
              {...props}
              {...headingProps}
            />
          ),
          h4: ({ node, ...props }: ComponentProps) => (
            <Heading
              as="h4"
              color={color || "fg"}
              fontFamily="heading"
              lineHeight="1.2"
              marginBottom={marginBottom || "0.25em"}
              marginTop={marginTop || 0}
              size={"md"}
              textWrapMode="initial"
              textWrapStyle="pretty"
              wordBreak="normal"
              wordWrap="initial"
              {...props}
              {...headingProps}
            />
          ),
          h5: ({ node, ...props }: ComponentProps) => (
            <Heading
              as="h5"
              color={color || "fg"}
              fontFamily="heading"
              lineHeight="1.25"
              marginBottom={marginBottom || "0.2em"}
              marginTop={marginTop || 0}
              size={"sm"}
              textWrapMode="initial"
              textWrapStyle="pretty"
              wordBreak="normal"
              wordWrap="initial"
              {...props}
              {...headingProps}
            />
          ),
          h6: ({ node, ...props }: ComponentProps) => (
            <Heading
              as="h6"
              color={color || "fg"}
              fontFamily="heading"
              lineHeight="1.3"
              marginBottom={marginBottom || "0.2em"}
              marginTop={marginTop || 0}
              size={"xs"}
              textWrapMode="initial"
              textWrapStyle="pretty"
              wordBreak="normal"
              wordWrap="initial"
              {...props}
              {...headingProps}
            />
          ),
          hr: ({ node, ...props }: ComponentProps) => (
            <Separator my={4} {...props} {...hrProps} />
          ),
          img: ({ node, ...props }: ComponentProps) =>
            renderMarkdownImage(props),
          ins: ({ node, ...props }: ComponentProps) => (
            <Text
              as="ins"
              color={color || "fg.read"}
              fontSize={fontSize || "md"}
              textDecorationColor="primary.500"
              textUnderlineOffset="0.18em"
              {...props}
            />
          ),
          li: (props) => {
            const { checked, children } = props as ComponentProps
            let checkboxNode = null
            if (checked !== null && checked !== undefined) {
              checkboxNode = (
                <Checkbox.Root checked={!!checked} disabled>
                  <Checkbox.HiddenInput />
                  <Checkbox.Control>
                    <Checkbox.Indicator />
                  </Checkbox.Control>
                  <Checkbox.Label>
                    <Text as="span" fontFamily="body">
                      {children}
                    </Text>
                  </Checkbox.Label>
                </Checkbox.Root>
              )
            }

            return (
              <Text
                as={List.Item}
                color={color || "fg.muted"}
                display="list-item"
                fontFamily="body"
                fontSize={fontSize || "md"}
                lineHeight="1.65"
                marginBottom={marginBottom || 0}
                marginTop={marginTop || 0}
                py={0}
                {...getCoreProps(props)}
                {...liProps}
              >
                {checkboxNode || children}
              </Text>
            )
          },
          mark: ({ node, ...props }: ComponentProps) => (
            <Text
              as="mark"
              bg={{ base: "yellow.200", _dark: "yellow.700" }}
              borderRadius="sm"
              color={{ base: "gray.950", _dark: "yellow.50" }}
              fontSize={fontSize || "md"}
              px="0.15em"
              {...props}
            />
          ),
          ol: (props) => {
            const { children, depth } = props as ComponentProps
            const attrs = getCoreProps(props)

            let styleType = "decimal"
            if (depth === 1) styleType = "lower-alpha"
            return (
              <List.Root
                as="ol"
                display="grid"
                gap={2}
                listStylePosition="outside"
                marginBottom={marginBottom || 0}
                marginTop={marginTop || 0}
                pl={6}
                py={0}
                styleType={styleType}
                w="full"
                width="100%"
                {...attrs}
                {...olProps}
              >
                {children}
              </List.Root>
            )
          },
          p: ({ children, node, ...props }: ComponentProps) => {
            const paragraphChildren = node?.children ?? []
            const imageIndex = paragraphChildren.findIndex(
              (child: any) =>
                child.type === "element" && child.tagName === "img"
            )
            const hasOnlyWhitespaceBeforeImage =
              imageIndex >= 0 &&
              paragraphChildren
                .slice(0, imageIndex)
                .every(
                  (child: any) =>
                    child.type === "text" &&
                    String(child.value || "").trim() === ""
                )
            const textAfterImage =
              imageIndex >= 0
                ? paragraphChildren
                    .slice(imageIndex + 1)
                    .map(getTextContentFromHast)
                    .join("")
                    .trim()
                : ""

            if (hasOnlyWhitespaceBeforeImage && textAfterImage) {
              return renderMarkdownImage(
                paragraphChildren[imageIndex].properties ?? {},
                textAfterImage
              )
            }

            const hasBlockChild = node?.children?.some(
              (child: any) =>
                child.type === "element" &&
                (child.tagName === "img" || child.tagName === "div")
            )
            if (hasBlockChild) {
              return (
                <Box
                  as="div"
                  color={color || "fg.read"}
                  fontFamily="body"
                  fontSize={fontSize || "md"}
                  lineHeight={{ base: "1.6", md: "1.6" }}
                  marginBottom={marginBottom || 0}
                  marginTop={marginTop || 0}
                  width="100%"
                  {...(pProps as any)}
                >
                  {children}
                </Box>
              )
            }
            return (
              <Text
                as="p"
                color={color || "fg.read"}
                fontFamily="body"
                fontSize={fontSize || "md"}
                lineHeight={{ base: "1.6", md: "1.6" }}
                marginBottom={marginBottom || 0}
                marginTop={marginTop || 0}
                // whiteSpace="pre-line"
                width="100%"
                {...props}
                {...pProps}
              >
                {children}
              </Text>
            )
          },
          section: ({ node, ...props }: ComponentProps) => (
            <Box
              as="section"
              borderTop="1px solid"
              borderTopColor="border"
              color={color || "fg.read"}
              fontSize={fontSize || "sm"}
              mt={8}
              pt={4}
              {...props}
            />
          ),
          strong: ({ node, ...props }: ComponentProps) => (
            <Text
              as="strong"
              color={color || "fg.read"}
              fontFamily="body"
              fontSize={fontSize || "md"}
              fontWeight="bold"
              {...props}
              {...strongProps}
            />
          ),
          sub: ({ node, ...props }: ComponentProps) => (
            <Text
              as="sub"
              color={color || "fg.read"}
              fontSize="0.75em"
              lineHeight="0"
              {...props}
            />
          ),
          sup: ({ node, ...props }: ComponentProps) => (
            <Text
              as="sup"
              color={color || "fg.read"}
              fontSize="0.75em"
              lineHeight="0"
              {...props}
            />
          ),
          table: ({ node, ...props }: ComponentProps) => (
            <Table.Root size="sm" variant="outline" {...props} {...tableProps}>
              {props.children}
            </Table.Root>
          ),
          tbody: ({ node, ...props }: ComponentProps) => (
            <Table.Body {...props} {...tbodyProps}>
              {props.children}
            </Table.Body>
          ),
          td: ({ node, ...props }: ComponentProps) => (
            <Table.Cell
              color={color || "fg.read"}
              fontFamily="body"
              fontSize={fontSize || "md"}
              {...props}
              {...tdProps}
            >
              {props.children}
            </Table.Cell>
          ),
          th: ({ node, ...props }: ComponentProps) => (
            <Table.ColumnHeader
              color={color || "fg.read"}
              fontFamily="body"
              fontSize={fontSize || "md"}
              {...props}
              {...thProps}
            >
              {props.children}
            </Table.ColumnHeader>
          ),
          thead: ({ node, ...props }: ComponentProps) => (
            <Table.Header {...props} {...theadProps}>
              {props.children}
            </Table.Header>
          ),
          tr: ({ node, ...props }: ComponentProps) => (
            <Table.Row {...props} {...trProps}>
              {props.children}
            </Table.Row>
          ),
          ul: (props) => {
            const { children, depth, ordered } = props as ComponentProps
            const attrs = getCoreProps(props)
            let styleType = "disc"
            if (ordered) {
              styleType = "decimal"
            }
            if (depth === 1) styleType = "circle"
            return (
              <List.Root
                as={ordered ? "ol" : "ul"}
                display="grid"
                gap={2}
                listStylePosition="outside"
                marginBottom={marginBottom || 0}
                marginTop={marginTop || 0}
                pl={6}
                py={0}
                styleType={styleType}
                w="full"
                {...attrs}
                {...ulProps}
              >
                {children}
              </List.Root>
            )
          },
        }}
        rehypePlugins={[rehypeRaw, rehypeStripScriptTags, rehypeFormat]}
        remarkPlugins={[
          remarkGfm,
          remarkDefinitionList,
          remarkAbbreviations,
          remarkEmoji,
          supersub,
          remarkIns,
          remarkFlexibleMarkers,
          remarkDirective,
          remarkCustomDirectives,
        ]}
        remarkRehypeOptions={{
          handlers: {
            ...defListHastHandlers,
          },
        }}
      >
        {renderedMarkdown}
      </ReactMarkdown>
    </Box>
  )
}

export default MarkdownParser
